import { ColumnState, FilterState } from 'ag-grid-community';
import { createReducer, on } from '@ngrx/store';
import { LocalAppStateActions as LocalAppStateActions } from '../store-types';
import { environment } from '@config/environment';
import { RowSpacingType } from '@shared/lib/models/row-spacing-type.enum';
import type { CaptureOptions } from '@chiron/app/analytic-engine/components/native-dashboards/models/capture-options.model';

export enum SessionPreference {
  HideWelcomeBanner = 'hideWelcomeBanner',
  HideHelpTip = 'hideHelpTip',
  HideDisclaimer = 'hideDisclaimer',
  HideAppNotice = 'hideAppNotice',
}
export enum UserPreference {
  HidePatientListDisclaimer = 'hidePatientListDisclaimer',
  HideEchoCasesDisclaimer = 'hideEchoCasesDisclaimer',
  HideRiskAuditsDisclaimer = 'hideRiskAuditsDisclaimer',
}

export interface LocalAppState {
  viewType: {
    [id: string]: string;
  };
  gridColumnState: {
    [gridId: string]: { state: ColumnState[]; timestamp: Date };
  };
  gridFilterState: {
    [gridId: string]: { state: FilterState; timestamp: Date };
  };
  sessionPreferences: {
    [key in SessionPreference]: boolean;
  };
  userPreferences: {
    [key in UserPreference]: boolean;
  };
  accessChangeComment: string;
  release: string;
  gridRowSpacing: {
    [gridId: string]: RowSpacingType;
  };
  gridRowStriping: {
    [gridId: string]: boolean;
  };
  // The native-dashboard Screenshot button's saved settings; null until the
  // user saves one, which is what makes the button fall back to the defaults.
  screenshotDefaults: CaptureOptions | null;
}
export const localAppStateFeatureKey = 'localAppState';
export const initialState: LocalAppState = {
  viewType: {},
  gridColumnState: {},
  gridFilterState: {},
  sessionPreferences: {
    [SessionPreference.HideWelcomeBanner]: false,
    [SessionPreference.HideHelpTip]: false,
    [SessionPreference.HideDisclaimer]: false,
    [SessionPreference.HideAppNotice]: false,
  },
  userPreferences: {
    [UserPreference.HidePatientListDisclaimer]: false,
    [UserPreference.HideEchoCasesDisclaimer]: false,
    [UserPreference.HideRiskAuditsDisclaimer]: false,
  },
  accessChangeComment: '',
  release: environment.release,
  gridRowSpacing: {},
  gridRowStriping: {},
  screenshotDefaults: null,
};
export const reducer = createReducer(
  initialState,
  on(LocalAppStateActions.saveViewTypeSuccess, (state, action) => ({
    ...state,
    viewType: {
      ...state.viewType,
      [action.id]: action.viewType,
    },
  })),
  on(LocalAppStateActions.saveGridFilterStateSuccess, (state, action) => ({
    ...state,
    gridFilterState: {
      ...state.gridFilterState,
      [action.gridId]: { state: action.state, timestamp: action.timestamp },
    },
  })),
  on(LocalAppStateActions.clearGridFilterStateSuccess, (state, action) => {
    const updatedGridFilterState = { ...state.gridFilterState };
    delete updatedGridFilterState[action.gridId];
    return {
      ...state,
      gridFilterState: updatedGridFilterState,
    };
  }),
  on(LocalAppStateActions.clearGridColumnStateSuccess, (state, action) => {
    const updatedGridColumnState = { ...state.gridColumnState };
    delete updatedGridColumnState[action.gridId];
    return {
      ...state,
      gridColumnState: updatedGridColumnState,
    };
  }),
  on(LocalAppStateActions.saveGridColumnStateSuccess, (state, action) => ({
    ...state,
    gridColumnState: {
      ...state.gridColumnState,
      [action.gridId]: {
        state: action.gridColumnState,
        timestamp: action.timestamp,
      },
    },
  })),
  on(LocalAppStateActions.hideWelcomeBannerSuccess, (state) => ({
    ...state,
    sessionPreferences: {
      ...state.sessionPreferences,
      [SessionPreference.HideWelcomeBanner]: true,
    },
  })),
  on(LocalAppStateActions.hideAppNoticeSuccess, (state) => ({
    ...state,
    sessionPreferences: {
      ...state.sessionPreferences,
      [SessionPreference.HideAppNotice]: true,
    },
  })),
  on(LocalAppStateActions.hideHelpTipSuccess, (state) => ({
    ...state,
    sessionPreferences: {
      ...state.sessionPreferences,
      [SessionPreference.HideHelpTip]: true,
    },
  })),
  on(LocalAppStateActions.hideDisclaimerSuccess, (state) => ({
    ...state,
    sessionPreferences: {
      ...state.sessionPreferences,
      [SessionPreference.HideDisclaimer]: true,
    },
  })),
  on(LocalAppStateActions.hidePatientListDisclaimerSuccess, (state) => ({
    ...state,
    userPreferences: {
      ...state.userPreferences,
      [UserPreference.HidePatientListDisclaimer]: true,
    },
  })),
  on(LocalAppStateActions.clearSessionPreferencesSuccess, (state) => ({
    ...state,
    sessionPreferences: initialState.sessionPreferences,
  })),
  on(LocalAppStateActions.loadLocalAppStateSuccess, (state, action) => ({
    ...state,
    ...action.localAppState,
  })),
  on(LocalAppStateActions.saveAccessChangeCommentSuccess, (state, action) => ({
    ...state,
    accessChangeComment: action.comment,
  })),
  on(LocalAppStateActions.saveGridRowSpacingSuccess, (state, action) => ({
    ...state,
    gridRowSpacing: {
      ...state.gridRowSpacing,
      [action.gridId]: action.size,
    },
  })),
  on(LocalAppStateActions.saveGridRowStripingSuccess, (state, action) => ({
    ...state,
    gridRowStriping: {
      ...state.gridRowStriping,
      [action.gridId]: action.striping,
    },
  })),
  on(LocalAppStateActions.saveScreenshotDefaultsSuccess, (state, action) => ({
    ...state,
    screenshotDefaults: action.defaults,
  }))
);
